/* eslint-disable react/require-default-props */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import { getUuid } from '@front10/helpers/dist/uuid';

import ErrorBoundary from '../../utils/ErrorBoundary';

import Box from '../../elements/Box';
import Text from '../../elements/Text';
import FareItemDepartureInfo from '../_commons/atoms/FareItemDepartureInfo';
import FareItemReturnInfo from '../_commons/atoms/FareItemReturnInfo';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import FareItemTripDuration from '../_commons/atoms/FareItemTripDuration';
import FareItemCallToAction from '../_commons/atoms/FareItemCallToAction';
import Separator from '../_commons/atoms/Separator';
import FareItemHotelName from '../_commons/atoms/FareItemHotelName';

import { handleFareItemInteraction } from '../_commons/commonFns';

const RateItemCard = (props) => {
  const {
    usedErrorBoundary,
    showDepartureInfo,
    showReturnInfo,
    showCallToAction,
    rateItemCallToActionContainer,
    separatorDatesProps,
    rateItemCheckingDate,
    rateItemCheckoutDate,
    rateItemPrice,
    rateItemTripDuration,
    rateBodyItem,
    rateFooterItem,
    rateItemCheckingCheckout,
    rateItemCallToAction,
    tabIndex,
    data,
    focusReferences,
    tabIndexCallToAction,
    rateItemCard,
    onFocus,
    rateItemRoomName,
    rateItemPropertyName,
    rateItemCountryName,
    ariaHidden,
    ariaLabel = '',
    flavorBodyAtoms = {
      PROPERTY_MODE: ['RateItemRoomName', 'RateItemDepartureReturnInfo'],
      CITY_PROPERTY: ['RateItemPropertyName', 'RateItemCountryName', 'RateItemDepartureReturnInfo']
    },
    flavorFooterAtoms = {
      PROPERTY_MODE: ['RateItemPrice', 'RateItemCallToAction', 'RateItemTripDuration'],
      CITY_PROPERTY: ['RateItemPrice', 'RateItemCallToAction', 'RateItemTripDuration']
    }
  } = props;

  let { availableAtomsBody, availableAtomsFooter } = props;
  const { flavor } = data;
  if (flavor) {
    availableAtomsBody = flavorBodyAtoms[flavor];
    availableAtomsFooter = flavorFooterAtoms[flavor];
  }

  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const id = getUuid();

  const {
    handleCallToAction,
    handleClick,
    handleItemKeyDown,
    handleCallToActionKeyDown,
    hasInteraction
  } = handleFareItemInteraction(props);

  const cursorClass = hasInteraction ? 'fr-cursor-pointer' : '';

  let elements = {};

  elements = {
    // RateItemPropertyName,
    // RateItemRoomName,
    // RateItemCountryName,
    // RateItemDepartureReturnInfo,
    // RateItemTripDuration,
    // RateItemPrice,
    // RateItemCallToAction
    RateItemPropertyName: <FareItemHotelName key="prop" {...data} {...rateItemPropertyName} />,
    RateItemCountryName: (
      <FareItemHotelName key="countryName" hotelName={data.countryName} {...rateItemCountryName} />
    ),
    RateItemRoomName: (
      <FareItemHotelName key="roomName" hotelName={data.roomName} {...rateItemRoomName} />
    ),
    RateItemDepartureReturnInfo: (
      <Box {...rateItemCheckingCheckout} key="dri">
        {showDepartureInfo && <FareItemDepartureInfo {...rateItemCheckingDate} {...data} />}
        {showDepartureInfo &&
          showReturnInfo &&
          data.formattedDepartureDate &&
          data.formattedReturnDate && <Separator {...separatorDatesProps} floatLeft />}
        {showReturnInfo && <FareItemReturnInfo {...rateItemCheckoutDate} {...data} />}
      </Box>
    ),
    RateItemTripDuration: <FareItemTripDuration key="dur" {...rateItemTripDuration} {...data} />,
    RateItemPrice: <FareItemPrice key="pri" {...rateItemPrice} {...data} />,
    RateItemCallToAction: (
      <Box key="cta" {...rateItemCallToActionContainer}>
        <FareItemCallToAction
          {...rateItemCallToAction}
          {...data}
          focusReferences={focusReferences}
          tabIndex={tabIndexCallToAction}
          onClick={handleCallToAction}
          onKeyDown={handleCallToActionKeyDown}
          ariaLabelledBy={`rate-item-card-${id}`}
        />
      </Box>
    )
  };

  const renderComponent = (
    <Box
      id={`rate-item-card-${id}`}
      position="relative"
      onClick={handleClick}
      onFocus={onFocus}
      onKeyDown={handleItemKeyDown}
      dataTestId="rateItemCard"
      tabIndex={tabIndex ? 0 : null}
      focusable={tabIndex}
      focusReferences={!showCallToAction ? focusReferences : null}
      role={!showCallToAction ? 'button' : null}
      {...rateItemCard}
      className={`fr-rate-item-card ${cursorClass} ${className} `}
      style={style}
      ariaHidden={ariaHidden ?? null}
      onMouseEnter={() => {
        if (props.onMouseEnter) {
          props.onMouseEnter();
        }
      }}
      onMouseLeave={() => {
        if (props.onMouseLeave) {
          props.onMouseLeave();
        }
      }}
    >
      {ariaLabel && <Text className="fr-sr-only">{ariaLabel}</Text>}
      <Box
        display="flex"
        flexDirection="column"
        {...rateBodyItem}
        className="fr-fare-item-card-body"
      >
        {availableAtomsBody.map((e) => elements[e])}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        {...rateFooterItem}
        className="fr-rate-item-card-footer"
      >
        {availableAtomsFooter.map((e) => elements[e])}
      </Box>
    </Box>
  );

  return usedErrorBoundary ? <ErrorBoundary>{renderComponent}</ErrorBoundary> : renderComponent;
};

RateItemCard.propTypes = {
  ariaHidden: PropTypes.bool,
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * OnClick event
   */
  onFocus: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * OnKeyDown event
   */
  onKeyDown: PropTypes.func,
  /**
   * Props of fareItemDate
   */
  fareItemDate: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemDepartureInfo
   */
  fareItemDepartureInfo: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemTripDuration
   */
  fareItemTripDuration: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemReturnInfo
   */
  fareItemReturnInfo: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemDeparture and fareItemReturn together
   */
  fareItemDepartureReturn: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemOriginDestination
   */
  fareItemOriginDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemPrice
   */
  fareItemPrice: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of tileBodyItem
   */
  fareBodyItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of footer wrapper div
   */
  fareFooterItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemCard
   */
  fareItemCard: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemCallToAction
   */
  fareItemCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemCallToActionContainer
   */
  fareItemCallToActionContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * Turn off or on tabIndex in FareItem
   */
  tabIndex: PropTypes.bool,
  /**
   * Define if the CallToAction will have focus for Screen Readers
   */
  tabIndexCallToAction: PropTypes.bool,
  /**
   * Function to handle focus renference of the component
   */
  focusReferences: PropTypes.func,
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Navigation Info of the pill
   */
  navigationInfo: PropTypes.string,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interactionHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  availableAtomsBody: PropTypes.arrayOf(
    PropTypes.oneOf([
      'RateItemPropertyName',
      'RateItemRoomName',
      'RateItemCountryName',
      'RateItemDepartureReturnInfo',
      'RateItemTripDuration',
      'RateItemPrice',
      'RateItemCallToAction'
    ])
  ),
  availableAtomsFooter: PropTypes.arrayOf(
    PropTypes.oneOf([
      'RateItemPropertyName',
      'RateItemRoomName',
      'RateItemCountryName',
      'RateItemDepartureReturnInfo',
      'RateItemTripDuration',
      'RateItemPrice',
      'RateItemCallToAction'
    ])
  ),
  ariaLabel: PropTypes.string
};

RateItemCard.defaultProps = {
  tabIndex: true,
  tabIndexCallToAction: true,
  onClick: () => {},
  callToActionOnClick: () => {},
  onKeyDown: () => {},
  interactionHotZone: 'smart',
  usedErrorBoundary: true,
  data: null,
  focusReferences: () => {},
  onFocus: () => {},
  availableAtoms: [
    'RateItemPropertyName',
    'RateItemCountryName',
    'RateItemDepartureReturnInfo',
    'RateItemTripDuration'
  ],
  availableAtomsFooter: ['RateItemPrice', 'RateItemCallToAction']
};

RateItemCard.componentName = 'RateItemCard';

export default RateItemCard;
